import React from 'react';
import { Container, Typography, Box, List, ListItem, ListItemText } from '@mui/material';
import { theme } from '../styles/theme'; // Assuming this path is correct

const RefundPolicy = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" component="h1" align="center" sx={{ color: theme.palette.primary.main, mb: 4 }}>
          Refund and Cancellation Policy
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          At Yogi Plus Water Tank, we want to make sure you're satisfied with your purchase. Here are our refund and cancellation policies to guide you through the process if you need to cancel an order or return a product.
        </Typography>

        {/* Section 1: Order Cancellation */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          1. Order Cancellation
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You can cancel your order at different stages:
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'decimal' }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Before Shipment:"
              secondary="You can cancel your order without any charges if it hasn't been dispatched from our warehouse. Simply log in to your account, go to 'My Orders,' and select the 'Cancel Order' option, or contact our customer support team immediately with your order details. A full refund will be initiated within 5-7 business days to your original payment method."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="After Shipment but Before Delivery:"
              secondary="If your order has been dispatched but not yet delivered, you may still cancel it. However, a nominal cancellation fee, typically covering shipping and handling charges, may be deducted from your refund. Please contact our customer support for assistance. The refund (minus applicable fees) will be processed after the product is returned to our warehouse and inspected, usually within 7-10 business days."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Custom or Special Orders:"
              secondary="Orders for custom-made or specially ordered water tanks may not be eligible for cancellation once production has begun. Please check the product description for specific terms related to such items."
            />
          </ListItem>
        </List>

        {/* Section 2: Returns Policy */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          2. Returns Policy
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We accept returns only under specific conditions within 7   days of delivery. To be eligible for a return, your item must be:
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Undamaged and Unused: The water tank must be in the same condition that you received it, unused, and in its original packaging." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Proof of Purchase: You must have the original receipt or proof of purchase." />
          </ListItem>
        </List>
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          We generally accept returns for the following reasons:
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'decimal' }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Damaged Product Received:"
              secondary="If you receive a damaged water tank, you must report it to us within 24-48 hours of delivery. Please provide clear photographs of the damage and the packaging. We will arrange for a return pickup and offer a replacement or a full refund."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Defective Product:"
              secondary="If the water tank is found to be defective upon installation or initial use (within the return window), please contact us with details and supporting evidence (e.g., video). We will arrange for an inspection and, if a manufacturing defect is confirmed, offer a replacement or refund."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Incorrect Product Received:"
              secondary="If you receive a product different from what you ordered, please notify us immediately. We will arrange for the correct product to be delivered and the incorrect one picked up at no extra charge to you."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Change of Mind / Unsuitable Product (Limited Cases):"
              secondary="Returns due to a change of mind or if the product is no longer suitable are generally not accepted for water tanks due to their bulky nature and specific handling requirements. In rare, approved cases, a restocking fee and return shipping costs may apply. Approval is at our sole discretion."
            />
          </ListItem>
        </List>
        <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
          To initiate a return: Please contact our customer support team at yogipluswatertank@gmail.com with your order number, reason for return, and any supporting images or videos. Our team will guide you through the return process.
        </Typography>

        {/* Section 3: Refund Process */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          3. Refund Process
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'decimal' }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Inspection:"
              secondary="Once your returned item is received and inspected, we will notify you of the approval or rejection of your refund. Please allow 2-3 business days for this process after the item reaches our facility."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Refund Approval:"
              secondary="If approved, your refund will be processed, and a credit will automatically be applied to your original method of payment, or a bank transfer will be initiated for COD orders, within 5-10 business days, depending on your bank's processing times."
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary="Partial Refunds:"
              secondary="In certain situations, only partial refunds are granted (e.g., product not in its original condition, damaged or missing parts for reasons not due to our error)."
            />
          </ListItem>
        </List>

        {/* Section 4: Non-Returnable Items */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          4. Non-Returnable Items
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          The following items are generally not eligible for return or refund:
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Products returned after the specified return window." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Products that have been used, installed, or are not in their original condition." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Products without original packaging, tags, or accessories." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Products damaged due to misuse or improper handling by the customer." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Items marked as 'final sale' or 'non-returnable'." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText primary="Custom-made or specially ordered water tanks (unless defective or damaged upon arrival)." />
          </ListItem>
        </List>

        {/* Section 5: Shipping Costs for Returns */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          5. Shipping Costs for Returns
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If the return is due to our error (e.g., damaged, defective, or incorrect product), we will bear the cost of return shipping. For returns due to other reasons (if approved, such as limited change of mind cases), the customer will be responsible for the return shipping costs, and a restocking fee may apply.
        </Typography>

        {/* Section 6: Contact Us */}
        <Typography variant="h4" component="h2" sx={{ color: theme.palette.text.primary, mt: 4, mb: 2 }}>
          6. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you have any questions about our Refund and Cancellation Policy, please contact us:
        </Typography>
        <List sx={{ ml: 2, listStyleType: 'none', p: 0 }}>
          <ListItem disablePadding>
            <ListItemText primary="Email: yogipluswatertank@gmail.com" />
          </ListItem>
        </List>

        <Typography variant="body2" align="center" sx={{ mt: 6, color: theme.palette.text.secondary }}>
          Last updated: June 10, 2025
        </Typography>
      </Container>
    </Box>
  );
};

export default RefundPolicy;